// overview-pinch — pure, DOM-free math for the overview band's pinch zoom.
// No imports; unit-tested. Mirrors the desktop zoom range
// (app/renderer/ui/rehearsal/overview-zoom.js) but exposes a continuous factor
// driven by a two-finger pinch instead of discrete levels.

/** Zoom bounds. 1× = whole song fits the band (no scroll); 12× = desktop max. */
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 12;

/** Clamp a zoom factor into [MIN_ZOOM, MAX_ZOOM]; non-finite → MIN_ZOOM. */
export function clampFactor(f: number): number {
  if (!Number.isFinite(f)) return f === Infinity ? MAX_ZOOM : MIN_ZOOM;
  if (f < MIN_ZOOM) return MIN_ZOOM;
  if (f > MAX_ZOOM) return MAX_ZOOM;
  return f;
}

export interface Point { x: number; y: number; }

/** Euclidean distance between two pointer positions. */
export function pointerDistance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Zoom factor for the current pinch: the factor at gesture start scaled by how
 * much the two fingers have spread (curDist / startDist), clamped to range.
 * An unusable start distance (≤0) leaves the factor untouched.
 */
export function factorFromPinch(startDist: number, curDist: number, startFactor: number): number {
  if (!(startDist > 0)) return clampFactor(startFactor);
  return clampFactor(startFactor * (curDist / startDist));
}

/**
 * scrollLeft that puts the current playhead at the LEFT edge of the visible
 * window: the playhead's x within the zoomed content is
 * (playheadSec / durationSec) × contentWidth. Never negative; 0 when duration
 * is non-positive.
 */
export function scrollLeftForPlayheadAtStart(playheadSec: number, durationSec: number, contentWidth: number): number {
  if (!(durationSec > 0)) return 0;
  const x = (playheadSec / durationSec) * contentWidth;
  return x > 0 ? x : 0;
}

/**
 * scrollLeft, при котором точка содержимого `anchorFrac` (0..1) остаётся под
 * пальцем, стоящим в `anchorOffsetPx` от левого края окна. Зум «вокруг пальцев»,
 * а не вокруг плейхеда — на паузе человек смотрит туда, где щиплет.
 */
export function scrollLeftForAnchor(anchorFrac: number, anchorOffsetPx: number, contentWidth: number, viewportWidth: number): number {
  const max = Math.max(0, contentWidth - viewportWidth);
  const x = anchorFrac * contentWidth - anchorOffsetPx;
  return Math.min(max, Math.max(0, x));
}

/** Подпись метки зума: «2×», «4×», «1.4×». */
export function zoomLabel(factor: number): string {
  if (factor >= 1.95) return `${Math.round(factor)}×`;
  return `${(Math.round(factor * 10) / 10).toFixed(1)}×`;
}
